import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { keyBy, sortBy } from 'lodash-es';
import * as React from 'react';
import { weakMapMemoize } from 'reselect';

import { useAppSelector } from 'vms/app/hooks';

import { selectCurrentUserId } from './selectors';
import type { User } from './types';

export const usersQuery = {
  queryKey: ['users'],
  queryFn: async ({ signal }: { signal?: AbortSignal }) => {
    const response = await axios.request<User[]>({
      url: '/accounts/',
      // The directory must include users from every location, so opt out of the
      // automatic location filter.
      params: { location: null },
      signal,
    });
    return response.data;
  },
};

export type UsersById = { [id: string]: User };

const EMPTY_USERS: UsersById = {};
const EMPTY_USER_LIST: User[] = [];

// `select` runs per observer, so share one lookup per fetched list across all rows.
const selectUsersById = weakMapMemoize((users: User[]): UsersById => keyBy(users, 'id'));
const selectSortedUsers = (users: User[]) => sortBy(users, 'name');

export function useUsers(): UsersById {
  return useQuery({ ...usersQuery, select: selectUsersById }).data ?? EMPTY_USERS;
}

export function useSortedUserList(): User[] {
  return useQuery({ ...usersQuery, select: selectSortedUsers }).data ?? EMPTY_USER_LIST;
}

// Ids already refetched for, so an id that stays unknown cannot loop refetches.
const refetchedMissingIds = new WeakMap<QueryClient, Set<string>>();

// Looks up one user so unrelated directory updates skip re-rendering. An unknown id
// usually means the user was created after the directory loaded, so refetch once.
export function useUser(id: string | null | undefined): User | null {
  const queryClient = useQueryClient();
  const select = React.useCallback(
    (users: User[]) => (id && selectUsersById(users)[id]) || null,
    [id]
  );
  const { data: user = null, isSuccess } = useQuery({ ...usersQuery, select });
  const isMissing = Boolean(id) && isSuccess && !user;
  React.useEffect(() => {
    if (!id || !isMissing) {
      return;
    }
    let refetched = refetchedMissingIds.get(queryClient);
    if (!refetched) {
      refetched = new Set();
      refetchedMissingIds.set(queryClient, refetched);
    }
    if (!refetched.has(id)) {
      refetched.add(id);
      queryClient.invalidateQueries({ queryKey: usersQuery.queryKey });
    }
  }, [id, isMissing, queryClient]);
  return user;
}

export function useCurrentUser(): User | null {
  return useUser(useAppSelector(selectCurrentUserId));
}

export function UserName({ id }: { id: string | null | undefined }) {
  return <>{useUser(id)?.name}</>;
}

export interface CurrentUserProps {
  currentUser: User | null;
}
